import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import BlogCard from "../Components/BlogCard";
import ReactMarkdown from 'react-markdown';
import BlogsSection from "../Components/BlogsSection";
import readButtonSvg from "../assets/vectors/read-now-btn-default.svg";
import readButtonHoverSvg from "../assets/vectors/read-now-btn-hover.svg";
import circleDecorationSvg from "../assets/vectors/circle-decoration.svg";

const BlogDetailPage = () => {
  const apiBaseUrl = process.env.NODE_ENV === 'development'
    ? 'http://localhost:1337'       // <--- URL for local development
    : 'https://strapi.genomii.ai'; // <--- URL for production
  const { documentId } = useParams(); // Changed from id to documentId
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll to email input function - will navigate back to main page and scroll
  const scrollToEmailInput = () => {
    navigate('/#email-signup');
    // Add a small delay to allow navigation to complete before scrolling
    setTimeout(() => {
      const emailElement = document.getElementById('email-signup');
      if (emailElement) {
        emailElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  useEffect(() => {
    const fetchBlogData = async () => {
      setLoading(true);
      try {
        // Fetch the specific blog using documentId with filter
        const blogResponse = await fetch(
          `${apiBaseUrl}/api/blogs?filters[documentId]=${documentId}&populate=*`
        );
        
        if (!blogResponse.ok) {
          throw new Error(`Failed to fetch blog: ${blogResponse.status}`);
        }
        
        const blogData = await blogResponse.json();
        
        if (!blogData.data || blogData.data.length === 0) {
          throw new Error("Blog not found");
        }
        
        // Use the first item since we're filtering by unique documentId
        setBlog(blogData.data[0]);
        
        // Fetch other blogs for the related section
        const allBlogsResponse = await fetch(
          `${apiBaseUrl}/api/blogs?populate=*&pagination[pageSize]=4`
        );
        
        if (!allBlogsResponse.ok) {
          throw new Error(`Failed to fetch related blogs: ${allBlogsResponse.status}`);
        }
        
        const allBlogsData = await allBlogsResponse.json();
        
        // Filter out the current blog and map to match BlogCard format
        const filteredBlogs = allBlogsData.data
          .filter(b => b.documentId !== documentId)
          .slice(0, 3) // Show maximum 3 related blogs
          .map(item => ({
            id: item.id,
            documentId: item.documentId,
            title: item.title,
            author: item.author_name || "Genomii",
            publisedAt: item.publishedAt || item.createdAt,
            imageUrl: item.cover_image?.url 
              ? item.cover_image.url
              : '/placeholder-image.jpg',
            url: `/blog/${item.documentId}`
          }));
        
        setRelatedBlogs(filteredBlogs);
      } catch (err) {
        console.error("Error fetching blog data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (documentId) {
      fetchBlogData();
      // Scroll to top when blog ID changes
      window.scrollTo(0, 0);
    }
  }, [documentId]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #DAF8FF 0%, #F6ECFD 100%)" }}>
        <Header onSignUpClick={scrollToEmailInput} />
        <div className="container mx-auto px-6 py-12 flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#355F5C]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #DAF8FF 0%, #F6ECFD 100%)" }}>
        <Header onSignUpClick={scrollToEmailInput} />
        <div className="container mx-auto px-6 py-12 min-h-[50vh]">
          <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Error Loading Blog</h2>
            <p>{error || "Blog not found"}</p>
            <button 
              onClick={() => navigate("/#blogs")} 
              className="mt-4 bg-[#355F5C] text-white px-6 py-2 rounded-lg hover:bg-[#2A4A48]"
            >
              Back to Blogs
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Prepare the cover image URL
  const coverImageUrl = blog.cover_image 
    ? blog.cover_image.url
    : '/placeholder-image.jpg';

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #DAF8FF 0%, #F6ECFD 100%)" }}>
      <Header onSignUpClick={scrollToEmailInput} />
      
      <main className="container mx-auto px-4 pt-20 pb-12">
        {/* Blog header */}
        <div className="max-w-4xl mx-auto mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" style={{
            background: "linear-gradient(89.85deg, #57A49D 0.12%, #A475C2 97.5%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "#034B6A",
            backgroundClip: "text",
          }}>
            {blog.title}
          </h1>
          
          {/* Author and date info */}
          <div className="flex items-center mb-8" style={{ color: "#034B6A" }}>
            <span className="font-medium italic">{blog.author_name || "Genomii"}</span>
            <span className="mx-2">•</span>
            <span>{formatDate(blog.publishedAt || blog.createdAt)}</span>
          </div>
          
          {/* Cover image */}
          {blog.cover_image && (
            <div className="mb-10">
              <img 
                src={coverImageUrl} 
                alt={blog.title}
                className="w-full h-auto object-cover rounded-lg shadow-md"
                style={{ maxWidth: '800px', maxHeight: '500px' }}
              />
            </div>
          )}
          
          {/* Blog content */}
          <div className="prose prose-lg max-w-none" style={{ color: "#034B6A" }}>
            <ReactMarkdown>{blog.main_body || ""}</ReactMarkdown>
          </div>
        </div>
      </main>
      
      {/* Related blogs section */}
      {relatedBlogs.length > 0 && (
        <div className="py-16 w-full" >
          <div className="container mx-auto px-6">
            <h2 className="text-4xl font-bold mb-12 text-center text-[#034B6A]">
              You May Also Like
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedBlogs.map((blog) => (
                <div key={blog.documentId}>
                  <BlogCard
                    blog={blog}
                    readButtonSvg={readButtonSvg}
                    readButtonHoverSvg={readButtonHoverSvg}
                    circleDecorationSvg={circleDecorationSvg}
                    onClick={() => navigate(`/blog/${blog.documentId}`)}
                  />
                </div>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={() => {navigate('/');
      
                  // Then scroll to the email section after a short delay to allow the page to load
                  setTimeout(() => {
                    const blogSection = document.getElementById('blogs');
                    if (blogSection) {
                      blogSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, 300);}}
                className="font-dm-sans bg-[#6DC5BD] text-white font-medium py-2 px-4 sm:py-3 sm:px-6 rounded-full transition-all hover:shadow-lg"
              >
                Browse All Blogs
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default BlogDetailPage;